import React, { useEffect } from "react";
import type { TFunction } from "i18next";
import { useQuery } from "react-query";
import { QueryKeys } from "@/network/queryKeys";
import { useMasterId } from "@/hooks/useMasterId";
import { SafedealAPI } from "@/api/safedeal";
import ms from "ms";
import { FORM_ID } from "../config";
import QueryRequest from "@/components/QueryRequest";
import { FormContext, useForm, useFormContext } from "react-hook-form";
import { safedealPayoutTiByCardSchema } from "@/validation/safe-deal";
import Empty from "@/components/Empty";
import isempty from "lodash/isEmpty";
import type { tRootForm, tByCardForm } from "./types";
import { useMut } from "@/hooks/useMut";
import { openLinkInSameTab } from "@/utils";
import { Button } from "_c/button";
import { AbstractControlledField } from "@/components/form/Field";

type tProps = {
  t: TFunction;
  ns: string;
  onSubmit: (form: tRootForm) => any;
};

const ByCard = (props: tProps) => {
  const masterId = useMasterId()!;
  const methods = useForm<tByCardForm>({
    defaultValues: {
      card_id: undefined,
    },
    validationSchema: safedealPayoutTiByCardSchema,
  });
  const ns = props.ns + "by_card.";
  const onSubmit = (form: tByCardForm) => props.onSubmit(form);
  const bindNewCardMutation = useMut(
    () =>
      SafedealAPI.tinkoffIndividuals.bindPayoutCard({ master_id: masterId }),
    {
      notifyOnError: true,
      t: props.t,
      onSuccess({ url }) {
        openLinkInSameTab(url);
      },
    }
  );
  return (
    <>
      <FormContext {...methods}>
        <form id={FORM_ID} onSubmit={methods.handleSubmit(onSubmit)}>
          <AbstractControlledField
            t={props.t}
            id="payout_card_id"
            name="card_id"
            Component={CardField}
            ns={ns}
            label={props.t(ns + "select_card")}
          />
        </form>
      </FormContext>
      <div
        css={`
          margin-top: 10px;
          text-align: center;
        `}
      >
        <Button
          type="button"
          disabled={bindNewCardMutation.isLoading}
          onClick={() => bindNewCardMutation.mutate()}
          small
          primary
        >
          {props.t(ns + "add_card")}
        </Button>
      </div>
    </>
  );
};

const CardField = ({
  ns,
  t,
  value,
}: {
  ns: string;
  t: TFunction;
  value?: string;
}) => {
  const masterId = useMasterId()!;
  const { setValue, register } = useFormContext<tByCardForm>();
  const changeCard = (cardId?: string) => {
    // @ts-ignore
    setValue("card_id", cardId);
  };
  useEffect(() => {
    // @ts-ignore
    register({ name: "card_id" });
  }, [register]);
  const cardsQuery = useQuery(
    [QueryKeys.tinkoffIndividualsBindedPayoutCards, { masterId }],
    () =>
      SafedealAPI.tinkoffIndividuals.getPayoutCards({ master_id: masterId }),
    {
      staleTime: 0,
      refetchInterval: ms("1 minute"),
      refetchOnWindowFocus: true,
      select: (d) => d?.cards.filter((c) => c.status === "active"),
    }
  );
  const removeCardMutation = useMut(
    (card_id: string) =>
      SafedealAPI.tinkoffIndividuals.removePayoutCard({
        master_id: masterId,
        card_id,
      }),
    {
      notifyOnError: true,
      t,
      confirm: true,
      onSuccess(_d, id) {
        if (id === value) {
          changeCard(undefined);
        }
        cardsQuery.refetch();
      },
    }
  );
  return (
    <QueryRequest query={cardsQuery}>
      {isempty(cardsQuery.data) ? (
        <Empty message={t(ns + "no_cards")} />
      ) : (
        <ul>
          {cardsQuery.data!.map((card) => {
            const isActive = card.external_id === value;
            return (
              <li
                css={`
                  margin-bottom: 15px;
                `}
                key={card.external_id}
              >
                <div
                  onClick={() => changeCard(card.external_id)}
                  role="button"
                  style={{
                    marginBottom: "5px",
                    cursor: "pointer",
                    display: "inline-block",
                    color: "white",
                    borderRadius: "var(--root-br)",
                    padding: "10px",
                    background: isActive
                      ? "var(--color-cta)"
                      : "var(--grad-primary-n)",
                  }}
                >
                  <div>{card.pan}</div>
                  {card.exp_date && (
                    <div
                      css={`
                        font-size: 14px;
                        margin-top: 10px;
                      `}
                    >
                      {card.exp_date.slice(0, 2) + "/" + card.exp_date.slice(2)}
                    </div>
                  )}
                </div>
                {/*<button
                    type="button"
                    onClick={() => changeCard(card.external_id)}
                    css={`
                      color: var(--color-cta);
                    `}
                  >
                    {t(ns + "select_card")}
                  </button>
                    */}
                <div>
                  <button
                    type="button"
                    disabled={removeCardMutation.isLoading}
                    onClick={() => removeCardMutation.mutate(card.external_id)}
                    css={`
                      font-size: 14px;
                      color: var(--color-danger);
                    `}
                  >
                    {t(ns + "rm_card")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </QueryRequest>
  );
};

export default ByCard;
